import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { CartLineModel, OrderLineModel } from "@insite/client-framework/Types/ApiModels";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CUSTOM_G1, CUSTOM_G2, CUSTOM_LENGTH, CUSTOM_PART_NUMBER, IS_CUSTOM_PRODUCT } from "@thkStore/Constants/ProductPropertiesKeys";
import React, { useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    product: CartLineModel | ProductContextModel | OrderLineModel;
    onClick?: React.EventHandler<React.MouseEvent>;
    extendedStyles?: ProductDescriptionStyles;
}

type Props = OwnProps;

export interface ProductDescriptionStyles {
    productDetailLink?: LinkPresentationProps;
    productDetailDescription?: TypographyProps;
    customPropertiesWrapper?: InjectableCss;
    customPropertyItem?: SmallHeadingAndTextStyles;
}

export const productDescriptionStyles: ProductDescriptionStyles = {
    productDetailLink: {
        css: css`
            width: 100%;
            background-color: ${({ theme }) => theme.colors.secondary.main};
            border-radius: 5px;
            padding: 15px 24px 10px;
            top: 0;
            
            &:hover {

                span {
                    color: ${({ theme }) => theme.colors.primary.main} !important;
                }
            }

            span{
                color: ${({ theme }) => theme.colors.text.main} !important;
                font-size: 18px;
                white-space: nowrap;
                text-overflow: ellipsis;
                overflow: hidden;
                
                svg {
                    color: ${({ theme }) => theme.colors.primary.contrast};
                    background-color: ${({ theme }) => theme.colors.primary.main};
                    width: 16px;
                    height: 16px;
                    flex-shrink: 0;
                    border-radius: 100%;
                    margin-right: 6px;
                }
            }
        `,
        typographyProps: {
            css: css`
                width: 100%;
                ${wrapInContainerStyles}
            `,
        },
    },
};

const ProductDescription = ({ product, onClick, extendedStyles }: Props) => {
    const [styles] = useState(() => mergeToNew(productDescriptionStyles, extendedStyles));    

    const isCustomProduct = (product as any)?.properties?.[IS_CUSTOM_PRODUCT] && JSON.parse((product as any).properties[IS_CUSTOM_PRODUCT].toLowerCase()) === true;

    const customProductDescription = isCustomProduct
            ? (product as any).properties[CUSTOM_PART_NUMBER] : null;
            
    const customProductInfo = isCustomProduct ? {
            length: (product as any).properties?.[CUSTOM_LENGTH],
            g1: (product as any).properties?.[CUSTOM_G1],
            g2: (product as any).properties?.[CUSTOM_G2],
        } : {};

    if (isCustomProduct && customProductDescription) {
        return (
            <StyledWrapper>
                <Typography {...styles.productDetailDescription}>{customProductDescription}</Typography>
                <StyledWrapper {...styles.customPropertiesWrapper}>
                    <SmallHeadingAndText {...styles.customPropertyItem} heading="Length" text={customProductInfo?.length ?? ""} />
                    <SmallHeadingAndText {...styles.customPropertyItem} heading="G1" text={customProductInfo?.g1 ?? ""} />
                    <SmallHeadingAndText {...styles.customPropertyItem} heading="G2" text={customProductInfo?.g2 ?? ""} />
                </StyledWrapper>
            </StyledWrapper>
        );
    }
    
    const description = "product" in product
            ? product.product.productTitle
            : product.shortDescription || ("description" in product ? product.description : "");
    const productDetailPath = "product" in product ? product.productInfo.productDetailPath : product.productUri;

    return productDetailPath && (!("isActiveProduct" in product) || product.isActiveProduct) ? (
        <Link
            {...styles.productDetailLink}
            href={productDetailPath}
            alt={`See product details for ${description}`}
            onClick={onClick}
            data-test-selector="productDescriptionLink"
        >
            <ChevronRight />
            {description}
        </Link>
    ) : (
        <Typography {...styles.productDetailDescription}>{description}</Typography>
    );
};

export default ProductDescription;